/**
 * useFilters — Manages all filter state and handlers for the School page.
 *
 * Extracted from school.tsx so the orchestrator stays slim.
 */

import { useState, useCallback } from 'react';
import { FilterCondition, FilterConfig } from '../components/AdvancedFilter';
import { QuickFilter, COMMON_QUICK_FILTERS } from '../components/QuickFilterChips';
import { FilterState, FilterActions } from '../types';

export interface UseFiltersReturn extends FilterState, FilterActions {}

export function useFilters(): UseFiltersReturn {
  // ── Primary filter values ──────────────────────────────────
  const [state, _setState] = useState('');
  const [region, _setRegion] = useState('');
  const [category, _setCategory] = useState('');
  const [kpi, _setKpi] = useState('');
  const [selectedInfraItems, _setSelectedInfraItems] = useState<string[]>([]);

  // ── Quick filters ──────────────────────────────────────────
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);

  // ── Advanced (global) filters ──────────────────────────────
  const [globalFilters, setGlobalFilters] = useState<FilterCondition[]>([]);
  const [savedFilters, setSavedFilters] = useState<FilterCondition[]>([]);
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const [filterConfigs, setFilterConfigs] = useState<FilterConfig[]>([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // ── Wrapped setters (category resets infra, etc.) ──────────
  const setState = useCallback((v: string) => _setState(v), []);
  const setRegion = useCallback((v: string) => _setRegion(v), []);

  const setCategory = useCallback((v: string) => {
    _setCategory(v);
    _setKpi(''); // reset kpi when category changes
    _setSelectedInfraItems([]); // reset infra selection when category changes
  }, []);

  const setKpi = useCallback((v: string) => _setKpi(v), []);
  const setSelectedInfraItems = useCallback((v: string[]) => _setSelectedInfraItems(v), []);

  // ── Reset everything ───────────────────────────────────────
  const handleReset = useCallback(() => {
    _setState('');
    _setRegion('');
    _setCategory('');
    _setKpi('');
    _setSelectedInfraItems([]);
    setActiveQuickFilters([]);
    setGlobalFilters([]);
    setSavedFilters([]);
  }, []);

  return {
    // state values
    state,
    region,
    category,
    kpi,
    selectedInfraItems,
    activeQuickFilters,
    globalFilters,
    savedFilters,
    filtersEnabled,
    filterConfigs,
    filterModalOpen,

    // actions
    setState,
    setRegion,
    setCategory,
    setKpi,
    setSelectedInfraItems,
    setActiveQuickFilters,
    setGlobalFilters,
    setSavedFilters,
    setFiltersEnabled,
    setFilterConfigs,
    setFilterModalOpen,
    handleReset,
  };
}
